/**
 * Squadre (V47): contenitori stabili nel tempo. Si creano, si rinominano, si
 * disattivano; non si cancellano, perche le stagioni passate devono restare
 * leggibili. Una squadra disattivata non riceve nuovi incroci.
 */

import { type FormEvent, useState } from 'react';
import { OctagonXIcon, PencilIcon } from 'lucide-react';
import { creaSquadraSchema, modificaSquadraSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ComandoIcona, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { useContesto } from '../navigazione/contesto.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import type { Squadra } from './matrice.js';

/** Null: dialog chiuso. Senza id: nuova squadra. */
type InModifica = Partial<Squadra> | null;

export function Squadre() {
  const { dati: squadre, errore, ricarica } = useDati<Squadra[]>('/amministrazione/squadre');
  const { ricaricaSquadre } = useContesto();
  const [inModifica, setInModifica] = useState<InModifica>(null);

  const nuova = <PulsanteNuovo etichetta="Nuova squadra" onClick={() => setInModifica({})} />;

  if (!squadre)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  return (
    <>
      <Testata titolo="Squadre" voce="squadre" azione={squadre.length > 0 && nuova} />
      <Corpo>
        {squadre.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessuna squadra</EmptyTitle>
              <EmptyDescription>
                Una squadra dura negli anni, come «Allievi»: la si aggancia poi a ogni stagione in
                cui gioca.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>{nuova}</EmptyContent>
          </Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="text-right">Stagioni</TableHead>
                <TableHead>
                  <span className="sr-only">Azioni</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {squadre.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {s.nome}
                      {!s.attiva && <Badge variant="secondary">Disattivata</Badge>}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{s.stagioni}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <ComandoIcona
                        etichetta={`Modifica ${s.nome}`}
                        Icona={PencilIcon}
                        size="icon-sm"
                        onClick={() => setInModifica(s)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={inModifica !== null}
          onOpenChange={(aperto) => !aperto && setInModifica(null)}
        >
          <DialogContent>
            {inModifica && (
              <FormSquadra
                key={inModifica.id ?? 'nuova'}
                squadra={inModifica}
                onChiudi={() => setInModifica(null)}
                onSalvata={async () => {
                  setInModifica(null);
                  // Nome e stato compaiono anche nel selettore delle squadre.
                  await Promise.all([ricarica(), ricaricaSquadre()]);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Corpo>
    </>
  );
}

function FormSquadra({
  squadra,
  onChiudi,
  onSalvata,
}: {
  squadra: Partial<Squadra>;
  onChiudi: () => void;
  onSalvata: () => Promise<void>;
}) {
  const [nome, setNome] = useState(squadra.nome ?? '');
  const [attiva, setAttiva] = useState(squadra.attiva ?? true);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = squadra.id
      ? modificaSquadraSchema.safeParse({ nome, attiva })
      : creaSquadraSchema.safeParse({ nome });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(
        squadra.id ? `/amministrazione/squadre/${squadra.id}` : '/amministrazione/squadre',
        { metodo: squadra.id ? 'PUT' : 'POST', corpo: esito.data },
      );
      await onSalvata();
      toast.success(`Squadra ${esito.data.nome ?? nome} salvata.`);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{squadra.id ? `Modifica ${squadra.nome}` : 'Nuova squadra'}</DialogTitle>
        <DialogDescription>Il nome non contiene l'anno, che è della stagione.</DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.nome)}>
          <FieldLabel htmlFor="squadra-nome">Nome</FieldLabel>
          <Input
            id="squadra-nome"
            value={nome}
            aria-invalid={Boolean(errori.nome)}
            onChange={(e) => setNome(e.target.value)}
          />
          <FieldError>{errori.nome}</FieldError>
        </Field>

        {squadra.id && (
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel htmlFor="squadra-attiva">Attiva</FieldLabel>
              <FieldDescription>
                Una squadra disattivata resta nello storico ma non si aggiunge a nuove stagioni.
              </FieldDescription>
            </FieldContent>
            <Switch id="squadra-attiva" checked={attiva} onCheckedChange={setAttiva} />
          </Field>
        )}
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {squadra.id ? 'Salva' : 'Crea squadra'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
